import JsSIP from 'jssip';
import VoiceAPI from './voiceAPIClient';

const createCallDisconnectedEvent = () => new CustomEvent('call:disconnected');

class SipVoiceClient extends EventTarget {
  constructor() {
    super();
    this.ua = null;
    this.activeSession = null;
    this.activeCallId = null;
    this.consultativeSession = null;
    this.consultativeCallId = null;
    this.inboxId = null;
    this.initialized = false;
    this._remoteAudio = null;
    this._consultativeAudio = null;
    this._held = false;
    JsSIP.debug.disable('JsSIP:*');

    // Clean up the SIP registration when the tab is really closing/reloading.
    // Without this, JsSIP leaves a stale contact on the SIP server (each UA
    // instance registers a unique contact), so reloads pile up registrations
    // and inbound calls fork to dead browser legs. Skip when the page is going
    // into the bfcache (persisted) — it may be restored with the UA intact.
    if (typeof window !== 'undefined') {
      window.addEventListener('pagehide', event => {
        if (!event.persisted) this.destroyDevice();
      });
    }
  }

  async initializeDevice(inboxId) {
    if (this.hasActiveConnection) return this.ua;
    if (this.inboxId === inboxId && this.ua?.isRegistered()) return this.ua;

    this.destroyDevice();

    const creds = await VoiceAPI.getSipCredentials(inboxId);
    if (!creds?.wss_url || !creds?.sip_username) {
      throw new Error('Invalid SIP credentials');
    }

    const socket = new JsSIP.WebSocketInterface(creds.wss_url);
    // Extract bare host from wss_url as fallback SIP domain so the URI never
    // contains a "wss://" scheme or port — those would break digest realm matching.
    const sipDomain =
      creds.sip_server ||
      creds.wss_url.replace(/^wss?:\/\//i, '').replace(/:\d+.*$/, '');

    // Stable instance ID per user+inbox (RFC 5626). Storing it in localStorage
    // ensures the same ID survives page refreshes, so the SIP registrar treats
    // each re-registration as an update to the existing contact rather than a
    // new one — eliminating duplicate registrations when tabs reload.
    const instanceKey = `sip_instance_${creds.sip_username}_${inboxId}`;
    let instanceId = localStorage.getItem(instanceKey);
    if (!instanceId) {
      instanceId = crypto.randomUUID();
      localStorage.setItem(instanceKey, instanceId);
    }

    const config = {
      sockets: [socket],
      uri: `sip:${creds.sip_username}@${sipDomain}`,
      password: creds.sip_password,
      authorization_user: creds.sip_username,
      register: true,
      register_expires: 300,
      instance_id: instanceId,
      session_timers: false,
    };

    this.ua = new JsSIP.UA(config);
    this.inboxId = inboxId;

    // JsSIP clears configuration.password = null after computing ha1 from the
    // REGISTER 401 challenge. The property is non-configurable (no setter can
    // be attached), but it is writable — so a plain assignment after REGISTER
    // completes restores the raw password for INVITE 407 re-auth when the
    // 407 realm differs from the REGISTER realm.
    const rawPassword = creds.sip_password;
    this.ua.on('registered', () => {
      this.ua.configuration.password = rawPassword;
    });

    this.ua.on('newRTCSession', event => this._handleIncomingSession(event));
    this.ua.start();
    this.initialized = true;

    return this.ua;
  }

  get hasActiveConnection() {
    return !!this.activeSession;
  }

  async answerCall() {
    if (!this.activeSession) return;

    const options = {
      mediaConstraints: { audio: true, video: false },
      pcConfig: {
        iceServers: [
          { urls: 'stun:stun.cloudflare.com:3478' },
          { urls: 'stun:stun.l.google.com:19302' },
        ],
      },
    };

    this.activeSession.answer(options);
  }

  makeCall(target, { conversationId = null } = {}) {
    if (!this.ua || !this.initialized) return null;

    // JsSIP fires 'peerconnection' synchronously inside ua.call() — before the
    // session is returned — so session.on('peerconnection', ...) registered
    // afterwards always misses the event. Pass the listener via eventHandlers so
    // JsSIP registers it before _createRTCConnection() emits the event.
    const options = {
      mediaConstraints: { audio: true, video: false },
      pcConfig: {
        iceServers: [
          { urls: 'stun:stun.cloudflare.com:3478' },
          { urls: 'stun:stun.l.google.com:19302' },
        ],
      },
      eventHandlers: {
        peerconnection: ({ peerconnection }) => {
          this._attachRemoteAudio(peerconnection);
        },
        icecandidate: ({ candidate, ready }) => {
          if (candidate?.type === 'srflx') ready();
        },
      },
    };

    const session = this.ua.call(`sip:${target}`, options);
    this.activeSession = session;
    this.activeCallId = session.id;

    // Register at dial time (status=ringing) so unanswered outbound calls land in
    // history too. Status PATCHes chain on this promise so a fast hangup can't
    // race the record creation. Without a conversation there is no backend
    // record, so registration stays null and status reports are skipped.
    const registration = conversationId
      ? VoiceAPI.registerOutboundSipCall(this.inboxId, {
          sipCallId: session.id,
          conversationId,
        }).catch(e => this._logApiError('register outbound call', e))
      : null;

    this._bindSessionEvents(session, { registration });
    return session;
  }

  holdCall() {
    if (!this.activeSession || this._held) return;
    this.activeSession.hold();
    this._held = true;
  }

  unholdCall() {
    if (!this.activeSession || !this._held) return;
    this.activeSession.unhold();
    this._held = false;
  }

  get isCallHeld() {
    return this._held;
  }

  get isConsultativeCallActive() {
    return !!this.consultativeSession;
  }

  // ─── Attended (consultative) transfer ────────────────────────────────────

  startConsultativeCall(target) {
    if (!this.ua || !this.initialized || !this.activeSession) return null;

    // Put the primary call on hold first
    if (!this._held) {
      this.activeSession.hold();
      this._held = true;
    }

    // Pause primary audio — primary call is on hold so its stream is silent,
    // but explicitly pausing avoids any residual audio bleed.
    if (this._remoteAudio) {
      this._remoteAudio.pause();
    }

    const domain = this.ua.configuration.uri.host;
    const sipTarget = target.includes('@')
      ? `sip:${target}`
      : `sip:${target}@${domain}`;

    const options = {
      mediaConstraints: { audio: true, video: false },
      pcConfig: {
        iceServers: [
          { urls: 'stun:stun.cloudflare.com:3478' },
          { urls: 'stun:stun.l.google.com:19302' },
        ],
      },
      eventHandlers: {
        peerconnection: ({ peerconnection }) => {
          this._attachConsultativeAudio(peerconnection);
        },
        icecandidate: ({ candidate, ready }) => {
          if (candidate?.type === 'srflx') ready();
        },
      },
    };

    const session = this.ua.call(sipTarget, options);
    this.consultativeSession = session;
    this.consultativeCallId = session.id;

    this._bindConsultativeSessionEvents(session);

    this.dispatchEvent(
      new CustomEvent('consult:started', { detail: { target } })
    );
    return session;
  }

  // Send REFER with Replaces to transfer the primary caller to the consultative target.
  completeAttendedTransfer() {
    if (!this.activeSession || !this.consultativeSession || !this.ua) return;

    try {
      const targetUri = this.consultativeSession.remote_identity.uri.toString();
      this.activeSession.refer(targetUri, {
        replaces: this.consultativeSession,
      });
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('[SIP] attended transfer REFER failed:', e);
    }

    // Clean up audio immediately; actual SIP sessions terminate via BYE from server
    this._cleanupConsultativeAudio();
    this._cleanupRemoteAudio();

    this.consultativeSession = null;
    this.consultativeCallId = null;
    this.activeSession = null;
    this._held = false;

    this.dispatchEvent(new CustomEvent('consult:ended'));
    this.dispatchEvent(createCallDisconnectedEvent());
  }

  // Cancel the consultative call and resume the primary call.
  cancelConsultativeTransfer() {
    if (!this.consultativeSession) return;

    try {
      this.consultativeSession.terminate();
      // eslint-disable-next-line no-empty
    } catch {}
    this._cleanupConsultativeAudio();
    this.consultativeSession = null;
    this.consultativeCallId = null;

    // Unhold the primary call and resume audio
    if (this.activeSession && this._held) {
      this.activeSession.unhold();
      this._held = false;
    }

    if (this._remoteAudio) {
      this._remoteAudio.play().catch(() => {});
    }

    this.dispatchEvent(new CustomEvent('consult:ended'));
  }

  // ─────────────────────────────────────────────────────────────────────────

  transferCall(target) {
    if (!this.activeSession || !this.ua) return;
    const uri = target.includes('@')
      ? `sip:${target}`
      : `sip:${target}@${this.ua.configuration.uri.host}`;
    this.activeSession.refer(uri);
  }

  endCall() {
    // Terminate consultative session first if one is active
    if (this.consultativeSession) {
      try {
        this.consultativeSession.terminate();
        // eslint-disable-next-line no-empty
      } catch {}
      this._cleanupConsultativeAudio();
      this.consultativeSession = null;
      this.consultativeCallId = null;
      this.dispatchEvent(new CustomEvent('consult:ended'));
    }

    if (this.activeSession) {
      this.activeSession.terminate();
    }
    this._cleanupRemoteAudio();
    this.activeSession = null;
    this._held = false;
  }

  destroyDevice() {
    if (this.consultativeSession) {
      try {
        this.consultativeSession.terminate();
        // eslint-disable-next-line no-empty
      } catch {}
      this._cleanupConsultativeAudio();
      this.consultativeSession = null;
      this.consultativeCallId = null;
    }

    if (this.ua) {
      this.ua.stop();
    }
    this._cleanupRemoteAudio();
    this.ua = null;
    this.activeSession = null;
    this.inboxId = null;
    this.initialized = false;
    this._held = false;
  }

  _attachRemoteAudio(peerconnection) {
    peerconnection.addEventListener('track', event => {
      if (event.track?.kind !== 'audio') return;
      // FreeSWITCH SDP answers often omit a=msid, so event.streams[0] can be
      // undefined. Fall back to wrapping the track in a new MediaStream.
      const stream = event.streams?.[0] || new MediaStream([event.track]);
      this._cleanupRemoteAudio();
      this._remoteAudio = new Audio();
      this._remoteAudio.srcObject = stream;
      this._remoteAudio.play().catch(() => {});
    });
  }

  _attachConsultativeAudio(peerconnection) {
    peerconnection.addEventListener('track', event => {
      if (event.track?.kind !== 'audio') return;
      const stream = event.streams?.[0] || new MediaStream([event.track]);
      this._cleanupConsultativeAudio();
      this._consultativeAudio = new Audio();
      this._consultativeAudio.srcObject = stream;
      this._consultativeAudio.play().catch(() => {});
    });
  }

  _cleanupRemoteAudio() {
    if (this._remoteAudio) {
      this._remoteAudio.pause();
      this._remoteAudio.srcObject = null;
      this._remoteAudio = null;
    }
  }

  _cleanupConsultativeAudio() {
    if (this._consultativeAudio) {
      this._consultativeAudio.pause();
      this._consultativeAudio.srcObject = null;
      this._consultativeAudio = null;
    }
  }

  _handleIncomingSession({ session, request, originator }) {
    // JsSIP fires newRTCSession for both incoming and outgoing sessions.
    // Skip our own outbound sessions — makeCall() and startConsultativeCall()
    // handle those directly.
    if (originator === 'local') return;

    // Reject the incoming INVITE if we already have an active session (e.g.
    // FreeSWITCH routing an outbound origination leg back to our own endpoint).
    if (this.activeSession) {
      session.terminate({ status_code: 486, reason_phrase: 'Busy Here' });
      return;
    }

    // Register audio and ICE handlers now — before answer() is called.
    // JsSIP may call setRemoteDescription (which fires 'track') synchronously
    // inside answer() before emitting 'peerconnection', so we attach the
    // peerconnection listener at session-creation time to guarantee we catch it.
    session.on('peerconnection', ({ peerconnection }) => {
      this._attachRemoteAudio(peerconnection);
    });
    session.on('icecandidate', ({ candidate, ready }) => {
      if (candidate?.type === 'srflx') ready();
    });

    const fromHeader = request.from;
    const callId = request.call_id;
    // uri.user is the unique SIP identifier (e.g. extension number); display_name
    // is the human-readable label sent by FreeSWITCH (e.g. "John Doe" or "1001").
    const fromNumber = fromHeader?.uri?.user || 'unknown';
    const displayName = fromHeader?.display_name?.trim() || null;

    this.activeSession = session;
    this.activeCallId = callId;

    const registration = VoiceAPI.registerSipCall(this.inboxId, {
      sipCallId: callId,
      from: fromNumber,
      displayName,
    }).catch(e => this._logApiError('register incoming call', e));

    this._bindSessionEvents(session, { registration });

    this.dispatchEvent(
      new CustomEvent('call:incoming', {
        detail: { callId, fromNumber: displayName || fromNumber, session },
      })
    );
  }

  _bindSessionEvents(session, { registration = Promise.resolve() } = {}) {
    const callId = this.activeCallId;
    const inboxId = this.inboxId;

    const reportStatus = payload => {
      if (!registration) return;
      registration.then(() =>
        VoiceAPI.updateSipCall(inboxId, callId, payload).catch(e =>
          this._logApiError(`report status ${payload.status}`, e)
        )
      );
    };

    session.on('accepted', () => {
      reportStatus({ status: 'in_progress' });
      this.dispatchEvent(
        new CustomEvent('call:accepted', { detail: { callSid: callId } })
      );
    });

    session.on('ended', event => {
      const cause = event.cause;
      const status = this._causeToStatus(cause);
      const duration =
        session.end_time && session.start_time
          ? Math.round((session.end_time - session.start_time) / 1000)
          : undefined;

      reportStatus({ status, duration_seconds: duration });

      this._cleanupRemoteAudio();
      this.activeSession = null;
      this.activeCallId = null;
      this.dispatchEvent(createCallDisconnectedEvent());
    });

    session.on('failed', event => {
      // eslint-disable-next-line no-console
      console.warn('[SIP] session failed:', event.cause, event);
      const status =
        event.cause === JsSIP.C.causes.CANCELED ? 'no_answer' : 'failed';

      reportStatus({ status });

      this._cleanupRemoteAudio();
      this.activeSession = null;
      this.activeCallId = null;
      this.dispatchEvent(createCallDisconnectedEvent());
    });
  }

  // Registration/status reporting failures were previously swallowed, which made
  // "call shows as missed" bugs invisible — keep them loud in the console.
  _logApiError(action, error) {
    // eslint-disable-next-line no-console
    console.warn(
      `[SIP] failed to ${action}:`,
      error?.response?.status,
      error?.response?.data || error?.message
    );
  }

  _bindConsultativeSessionEvents(session) {
    session.on('accepted', () => {
      this.dispatchEvent(new CustomEvent('consult:connected'));
    });

    session.on('ended', () => {
      // Remote party hung up the consultative call — unhold primary and resume
      this._cleanupConsultativeAudio();
      this.consultativeSession = null;
      this.consultativeCallId = null;

      if (this.activeSession && this._held) {
        this.activeSession.unhold();
        this._held = false;
      }
      if (this._remoteAudio) {
        this._remoteAudio.play().catch(() => {});
      }

      this.dispatchEvent(new CustomEvent('consult:ended'));
    });

    session.on('failed', () => {
      this._cleanupConsultativeAudio();
      this.consultativeSession = null;
      this.consultativeCallId = null;

      if (this.activeSession && this._held) {
        this.activeSession.unhold();
        this._held = false;
      }
      if (this._remoteAudio) {
        this._remoteAudio.play().catch(() => {});
      }

      this.dispatchEvent(new CustomEvent('consult:ended'));
    });
  }

  _causeToStatus(cause) {
    // JsSIP.C.causes.BYE ('Terminated') is the cause for a normal call end via
    // BYE (either party hung up). NORMAL_CLEARING does not exist in this version
    // of JsSIP, so checking for it would never match.
    if (!cause || cause === JsSIP.C.causes.BYE) return 'completed';
    if (cause === JsSIP.C.causes.CANCELED || cause === JsSIP.C.causes.NO_ANSWER)
      return 'no_answer';
    return 'failed';
  }
}

const sipVoiceClient = new SipVoiceClient();
if (typeof window !== 'undefined') window._sipClient = sipVoiceClient;
export default sipVoiceClient;
